import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Users, MessageCircle, UserPlus, Trash2, ArrowLeftRight } from 'lucide-react';

function FamilyConstellation({ onSave, savedData }) {
  const [members, setMembers] = useState(savedData?.members || []);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [selectedListener, setSelectedListener] = useState(null);
  const [dialogue, setDialogue] = useState(savedData?.dialogue || []);
  const [currentMessage, setCurrentMessage] = useState('');
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('');

  const commonRoles = [
    'Father', 'Mother', 'Partner/Spouse', 'Child', 'Sibling', 
    'Best Friend', 'Boss', 'Coworker', 'Teacher', 'Mentor',
    'Inner Child', 'Future Self', 'Past Self'
  ];

  useEffect(() => {
    if (onSave) {
      onSave({ members, dialogue });
    }
  }, [members, dialogue]);

  const addMember = () => {
    if (!newMemberName || !newMemberRole) return;
    
    const newMember = {
      id: Date.now().toString(),
      name: newMemberName,
      role: newMemberRole,
      color: `hsl(${Math.random() * 360}, 70%, 85%)`
    };
    
    setMembers([...members, newMember]);
    setNewMemberName('');
    setNewMemberRole('');
  };

  const removeMember = (id) => {
    setMembers(members.filter(m => m.id !== id));
    if (selectedSpeaker?.id === id) setSelectedSpeaker(null);
    if (selectedListener?.id === id) setSelectedListener(null);
  };

  const addToDialogue = () => {
    if (!selectedSpeaker || !selectedListener || !currentMessage.trim()) return;
    
    const entry = {
      id: Date.now().toString(),
      speaker: selectedSpeaker,
      listener: selectedListener,
      message: currentMessage,
      timestamp: new Date().toISOString()
    };
    
    setDialogue([...dialogue, entry]);
    setCurrentMessage('');
  };

  const switchRoles = () => {
    const temp = selectedSpeaker;
    setSelectedSpeaker(selectedListener);
    setSelectedListener(temp);
  };

  return (
    <div className="space-y-8">
      {/* About Constellation Work */}
      <div className="p-6 rounded-sm" style={{ backgroundColor: 'hsl(138 25% 96%)', border: '2px solid hsl(138 30% 40%)' }}>
        <h3 className="text-2xl font-bold mb-3">Family Constellation Practice</h3>
        <p className="text-base leading-relaxed text-gray-700">
          Inspired by <strong>family constellation therapy</strong>, this practice helps you explore relationships by embodying different perspectives. 
          Speak as yourself to others in your life, then switch roles and respond as them. This deepens empathy and reveals unmet needs.
        </p>
        <p className="text-sm text-gray-600 mt-3 italic">
          Based on Steve Torma's integration of NVC with relational healing practices.
        </p>
      </div>

      {/* Add Constellation Members */}
      <Card style={{ borderRadius: 0 }}>
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <UserPlus className="w-6 h-6" />
            Add People in Your Constellation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newMemberRole} onValueChange={setNewMemberRole}>
                <SelectTrigger className="border-black" style={{ borderRadius: 0 }}>
                  <SelectValue placeholder="Select role..." />
                </SelectTrigger>
                <SelectContent>
                  {commonRoles.map(role => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                placeholder="e.g., John, Mom Sarah, etc."
                value={newMemberName}
                onChange={(e) => setNewMemberName(e.target.value)}
                className="border-black"
                style={{ borderRadius: 0 }}
              />
            </div>
            <div className="flex items-end">
              <Button 
                onClick={addMember}
                className="bg-black text-white hover:bg-black/90 w-full"
                style={{ borderRadius: 0 }}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>
          </div>

          {/* Show Added Members */}
          {members.length > 0 && (
            <div className="mt-6 space-y-2">
              <p className="text-sm font-semibold text-gray-600">Your Constellation:</p>
              <div className="flex flex-wrap gap-2">
                {members.map(member => (
                  <div 
                    key={member.id} 
                    className="px-4 py-2 border-2 border-black flex items-center gap-2"
                    style={{ backgroundColor: member.color }}
                  >
                    <span className="font-semibold">{member.role}:</span>
                    <span>{member.name}</span>
                    <button 
                      onClick={() => removeMember(member.id)}
                      className="ml-2 text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogue Practice */}
      {members.length >= 2 && (
        <Card style={{ borderRadius: 0, border: '3px solid black' }}>
          <CardHeader style={{ backgroundColor: 'hsl(138 20% 90%)' }}>
            <CardTitle className="text-2xl flex items-center gap-2">
              <MessageCircle className="w-6 h-6" />
              Constellation Dialogue Practice
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 mt-6">
            {/* Select Speaker and Listener */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Speaking as:</Label>
                <Select value={selectedSpeaker?.id} onValueChange={(id) => setSelectedSpeaker(members.find(m => m.id === id))}>
                  <SelectTrigger className="border-black" style={{ borderRadius: 0 }}>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.role}: {m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end justify-center">
                <Button
                  onClick={switchRoles}
                  variant="outline"
                  className="border-black"
                  style={{ borderRadius: 0 }}
                  disabled={!selectedSpeaker || !selectedListener}
                >
                  <ArrowLeftRight className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Speaking to:</Label>
                <Select value={selectedListener?.id} onValueChange={(id) => setSelectedListener(members.find(m => m.id === id))}>
                  <SelectTrigger className="border-black" style={{ borderRadius: 0 }}>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {members.filter(m => m.id !== selectedSpeaker?.id).map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.role}: {m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Current Message Input */}
            {selectedSpeaker && selectedListener && (
              <div className="space-y-3">
                <Label className="text-lg">
                  <strong>{selectedSpeaker.role} ({selectedSpeaker.name})</strong> says to <strong>{selectedListener.role} ({selectedListener.name})</strong>:
                </Label>
                <Textarea
                  placeholder="Express using OFNR... When I observe... I feel... because I need... Would you be willing to...?"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  className="min-h-[120px] text-lg border-black"
                  style={{ borderRadius: 0 }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      addToDialogue();
                    }
                  }}
                />
                <div className="flex gap-3">
                  <Button 
                    onClick={addToDialogue}
                    className="bg-black text-white hover:bg-black/90"
                    style={{ borderRadius: 0 }}
                  >
                    Add to Dialogue (Ctrl+Enter)
                  </Button>
                  <Button 
                    onClick={switchRoles}
                    variant="outline"
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  >
                    <ArrowLeftRight className="w-4 h-4 mr-2" />
                    Switch Roles
                  </Button>
                </div>
              </div>
            )}

            {/* Dialogue History */}
            {dialogue.length > 0 && (
              <div className="mt-8 space-y-4">
                <h4 className="text-xl font-bold">Constellation Dialogue:</h4>
                <div className="space-y-3 max-h-[600px] overflow-y-auto p-4 border-2 border-gray-300 rounded-sm">
                  {dialogue.map((entry, idx) => (
                    <div 
                      key={entry.id}
                      className="p-4 border-l-4"
                      style={{ 
                        backgroundColor: entry.speaker.color,
                        borderLeftColor: 'black'
                      }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-bold text-sm">
                          {entry.speaker.role} ({entry.speaker.name}) → {entry.listener.role} ({entry.listener.name})
                        </p>
                        <p className="text-xs text-gray-600">{new Date(entry.timestamp).toLocaleTimeString()}</p>
                      </div>
                      <p className="text-base leading-relaxed">{entry.message}</p>
                    </div>
                  ))}
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      const text = dialogue.map(d => 
                        `${d.speaker.role} (${d.speaker.name}) to ${d.listener.role} (${d.listener.name}):\n${d.message}\n`
                      ).join('\n---\n');
                      navigator.clipboard.writeText(text);
                      alert('Dialogue copied to clipboard!');
                    }}
                    variant="outline"
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  >
                    Copy Dialogue
                  </Button>
                  <Button
                    onClick={() => setDialogue([])}
                    variant="outline"
                    className="border-red-600 text-red-600"
                    style={{ borderRadius: 0 }}
                  >
                    Clear Dialogue
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Guidance */}
      <div className="p-6 bg-gray-50 border-l-4 border-black">
        <h4 className="font-bold mb-2">How to Use Constellation Practice:</h4>
        <ol className="space-y-2 text-sm list-decimal list-inside">
          <li>Add people from your life (family members, friends, parts of yourself)</li>
          <li>Select who is speaking and who is listening</li>
          <li>Express using NVC (OFNR format recommended)</li>
          <li>Switch roles and respond AS that person - what might they feel/need?</li>
          <li>Notice what arises as you embody different perspectives</li>
          <li>This reveals unmet needs and pathways to healing</li>
        </ol>
        <p className="text-sm italic mt-3 text-gray-700">
          "When we can hold multiple perspectives with compassion, transformation becomes possible." - Steve Torma's teaching
        </p>
      </div>
    </div>
  );
}

export default FamilyConstellation;
